/**
 * Shared resolver for "what dataset(s) is this run actually using?"
 *
 * Used by:
 *   - /configs Session Preview        — live selection (selectedSites)
 *   - /execute Configuration Details  — saved snapshot (currentConfig.sites)
 *
 * Goal: collapse the per-site capability_id tokens that flow through the
 * legacy `dataset_path` / `selected_datasets` fields (or the new site-local
 * `sites[]` array) into a human-readable list of LOGICAL dataset names.
 *
 * A federation of 5 sites all hosting `Primary Site Oncology` should read as
 * a single "Primary Site Oncology" line, not as 5 per-site capability_id
 * tokens like `cap_tgh_tabular_primary`.
 */

import { mockDatasetDefinitions } from "../data/mockDatasetDefinitions";

const resolveByRef = (datasetRef, capabilityDisplayName) => {
  const def = mockDatasetDefinitions[datasetRef];
  return def?.display_name || capabilityDisplayName || datasetRef;
};

// Ground-truth observation (browser-console diagnostic, 2026-06-10):
// backend's create_training_run strips `sites[]` entirely. The only fields
// it persists into config_data.dataset_config that reflect the user's real
// choice are `processing_type` and `model_type` — everything else
// (`selected_datasets`, `dataset_path`) is a zombie default like
// `dataset_primary_site_c1.csv` regardless of what the user picked.
//
// Reverse-infer the user's dataset by intersecting processing_type with
// model_type against mockDatasetDefinitions.compatible_models:
//   • imaging_classification: ProstateX uses 3D models (ResNet18_3D /
//     ConvNeXt_3D / SwinTiny_3D / SimpleCNN); LIDC uses 2D (ConvNeXt /
//     ResNet18 / Swin Transformer). Disjoint → uniquely identifies.
//   • imaging_segmentation: both ProstateX-seg and LIDC-seg accept
//     UNet/UNet++/FPN. Ambiguous → caller falls back.
//   • tabular: all five tabular defs share the same model list. Ambiguous.
//
// Returns the unique display_name or null when ambiguous / no match.
const normalizeForCompare = (s) =>
  String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const PT_TO_REFS = {
  tabular: [
    "tabular/primary_site_oncology_v1",
    "tabular/omop_tumor_cell_range_v1",
    "tabular/brca_survival_v1",
    "tabular/therapy_type_v1",
    "tabular/treatment_outcome_v1",
  ],
  imaging_classification: ["prostatex_cls_v1", "lidc_cls_v1"],
  imaging_segmentation: ["prostatex_seg_v1", "lidc_seg_v1"],
};

const PT_FRIENDLY = {
  tabular: "Tabular Dataset",
  imaging_classification: "Imaging Classification",
  imaging_segmentation: "Imaging Segmentation",
};

const inferDatasetFromConfigData = (cd) => {
  const dc = cd?.dataset_config || cd || null;
  if (!dc) return null;
  const pt = dc.processing_type;
  const refsForPT = PT_TO_REFS[pt] || [];
  if (refsForPT.length === 0) return null;

  const mt = normalizeForCompare(dc.model_type);
  if (!mt) return null;

  const hits = [];
  for (const ref of refsForPT) {
    const def = mockDatasetDefinitions[ref];
    if (!def) continue;
    const models = def.compatible_models || [];
    if (models.some((m) => normalizeForCompare(m) === mt)) {
      hits.push(def.display_name);
    }
  }
  if (hits.length === 1) return hits[0];
  // Ambiguous (e.g. UNet matches both ProstateX-seg and LIDC-seg) — caller
  // falls back to processing_type friendly label.
  return null;
};

// Model-name-only inference. Used when config_data hasn't been enriched
// yet (the /api/user/training-summary slim list omits config_data, so the
// per-row detail fetch may still be in flight or capped out by the
// browser's connection pool). The slim row always carries `model_type`
// in `execution.modelType`, and that one field is enough for everything
// except segmentation (UNet/UNet++/FPN are shared between ProstateX-seg
// and LIDC-seg) and tabular (all five tabular defs share the same model
// list — degrades cleanly to "Tabular Dataset").
const inferDatasetFromModelOnly = (modelType) => {
  const mt = normalizeForCompare(modelType);
  if (!mt) return null;
  const hits = [];
  for (const [ref, def] of Object.entries(mockDatasetDefinitions)) {
    const models = def.compatible_models || [];
    if (models.some((m) => normalizeForCompare(m) === mt)) {
      hits.push({ ref, display_name: def.display_name });
    }
  }
  if (hits.length === 0) return null;
  if (hits.length === 1) return hits[0].display_name;
  const allTabular = hits.every((h) => h.ref.startsWith('tabular/'));
  if (allTabular) return PT_FRIENDLY.tabular;
  const allSeg = hits.every((h) => h.ref.endsWith('_seg_v1'));
  if (allSeg) return PT_FRIENDLY.imaging_segmentation;
  const allCls = hits.every((h) => h.ref.endsWith('_cls_v1'));
  if (allCls) return PT_FRIENDLY.imaging_classification;
  return null;
};

/**
 * Resolve dataset display names from any of the three inputs the prototype
 * uses to describe a run's dataset(s):
 *
 *   sites             — preferred. Array of `{site_id, capability_id}`.
 *                       Comes from AppContext.selectedSites (live) or
 *                       currentConfig.sites (saved snapshot).
 *   selectedDatasets  — fallback. Array of capability_id strings used by
 *                       the legacy simulation flow.
 *   siteCapabilities  — `{[site_id]: SiteDatasetCapability[]}` map from
 *                       AppContext, used to resolve sites[] entries.
 *   availableDatasets — flat array of SiteDatasetCapability objects
 *                       (AppContext.availableDatasets), used to resolve
 *                       legacy selectedDatasets entries.
 *
 * Returns:
 *   {
 *     names:  string[]   — deduped logical dataset display names
 *     source: "site-local" | "legacy" | "empty"
 *   }
 *
 * Resolution order:
 *   1. If sites[] has any complete (site_id + capability_id) entries that
 *      resolve via siteCapabilities → dataset_ref, use those (source = "site-local").
 *   2. Else if selectedDatasets[] has entries, resolve each via
 *      availableDatasets → dataset_ref (source = "legacy"). Untyped entries
 *      that don't resolve fall back to their tail path segment so legacy
 *      file-path entries still render cleanly.
 *   3. Else return { names: [], source: "empty" }.
 */
/**
 * Pick a friendly display name for a single executionHistory row.
 *
 * Execution rows arrive from /api/user/training-summary with `datasetPath`
 * (often a backend-default literal like "dataset_primary_site_c1.csv" that
 * doesn't reflect what the run actually used) and an optional `datasetName`
 * (sometimes a friendly name, sometimes also a path). The slim list response
 * does NOT include `config_data`, so the per-row detail endpoint
 * `/api/user/training-runs/<id>` is what carries the rich shape; AppContext
 * enriches each row with `config_data` after the summary loads.
 *
 * Resolution order:
 *   1. execution.datasetName, if it looks human-readable (no '/' and no
 *      common data-file extension)
 *   2. Try the rich sites[]/selected_datasets paths inside the saved
 *      config_data — same resolver that drives the Configuration Details
 *      Dataset row. Joined with ', ' so a federated run across multiple
 *      datasets reads as "BRCA Survival, ProstateX Classification" rather
 *      than just the first one.
 *   3. mockDatasetDefinitions[<candidate>].display_name where candidate
 *      cycles through every dataset-ish field the row exposes
 *   4. Last segment of datasetPath (matches the pre-fix behavior)
 *   5. 'Unknown'
 */
export const resolveDatasetDisplayNameForExecution = (
  execution,
  { siteCapabilities, availableDatasets } = {}
) => {
  if (!execution) return 'Unknown';

  const name = execution.datasetName;
  if (
    name &&
    typeof name === 'string' &&
    !name.includes('/') &&
    !/\.(csv|json|parquet|tsv)$/i.test(name)
  ) {
    return name;
  }

  // Saved config_data is the authoritative source for "what dataset(s) did
  // this run actually use" — same shape the live Configuration Details panel
  // consumes (TrainingStatusCard.jsx:483, via resolveDatasetDisplayNames).
  //
  // handleSave (configs/index.jsx:625-635) writes `sites` at the TOP LEVEL
  // of config_data, NOT nested under `dataset_config`. The earlier version
  // of this resolver only looked at `dc.sites` and missed every site-local
  // run; coalesce both nestings here.
  //
  // We DELIBERATELY do not fall back to `dc.selected_datasets` for
  // file-path-style entries: the backend stamps stale simulation defaults
  // (e.g. `dataset_primary_site_c1.csv, dataset_primary_site_c2.csv`)
  // into that field regardless of what the user actually picked — same
  // reason TrainingStatusCard.jsx:466-475 stopped trusting it. The
  // capability_id-keyed branch of resolveDatasetDisplayNames still tries
  // legitimate selected_datasets entries that look like capability_ids.
  const cd = execution.config_data || null;
  const dc = cd?.dataset_config || null;
  const sitesCandidate = dc?.sites || cd?.sites || null;
  const selectedCandidate = dc?.selected_datasets || cd?.selected_datasets || null;
  if (cd || dc) {
    const { names } = resolveDatasetDisplayNames({
      sites: sitesCandidate,
      selectedDatasets: selectedCandidate,
      siteCapabilities,
      availableDatasets,
    });
    // resolveDatasetDisplayNames returns `source: "site-local"` when sites
    // resolved through siteCapabilities, `"legacy"` when capability_ids in
    // selected_datasets resolved, and `"empty"` otherwise. We accept the
    // first two; legacy file-path-only entries (the zombie defaults) end
    // up surfaced as their tail by the helper — we filter those out below.
    const filtered = (names || []).filter((n) => {
      if (!n || typeof n !== 'string') return false;
      if (/\.(csv|json|parquet|tsv)$/i.test(n)) return false;
      return true;
    });
    if (filtered.length > 0) {
      return filtered.join(', ');
    }
  }

  // Reverse-infer from (processing_type, model_type). This is the only
  // path that works for runs persisted by the current backend, which
  // strips sites[] from saved config_data — see header comment on
  // inferDatasetFromConfigData. Resolves correctly for imaging
  // classification (3D vs 2D models disjoint); returns null when
  // ambiguous so we fall through to the generic category label.
  const inferred = inferDatasetFromConfigData(cd);
  if (inferred) return inferred;

  // config_data may still be enriching (per-row detail fetches queue
  // behind the browser's per-origin connection cap when there are many
  // historic rows). The slim summary row reliably carries `modelType`
  // — most model names map to exactly one dataset_ref, so we can still
  // surface "ProstateX Classification" without waiting for enrichment.
  const inferredFromModel = inferDatasetFromModelOnly(execution.modelType);
  if (inferredFromModel) return inferredFromModel;

  const candidates = [
    execution.selected_datasets?.[0],
    dc?.dataset_path,
    dc?.selected_datasets?.[0],
    cd?.dataset_path,
    execution.datasetPath,
  ].filter(Boolean);
  for (const ref of candidates) {
    const def = mockDatasetDefinitions[ref];
    if (def?.display_name) return def.display_name;
  }

  // Last-resort: at least surface the run category instead of file paths
  // or a useless "Unknown" — strictly better than dataset_primary_site_c*.csv.
  const pt = dc?.processing_type || cd?.processing_type || execution.processing_type;
  if (pt && PT_FRIENDLY[pt]) return PT_FRIENDLY[pt];

  return 'Unknown Dataset';
};

export const resolveDatasetDisplayNames = ({
  sites,
  selectedDatasets,
  siteCapabilities,
  availableDatasets,
} = {}) => {
  const completedSites = (sites || []).filter(
    (s) => s && s.site_id && s.capability_id
  );

  if (completedSites.length > 0 && siteCapabilities) {
    const dedupedByRef = new Map();
    for (const sel of completedSites) {
      const caps = siteCapabilities[sel.site_id] || [];
      const cap = caps.find((c) => c.capability_id === sel.capability_id);
      if (cap?.dataset_ref && !dedupedByRef.has(cap.dataset_ref)) {
        dedupedByRef.set(
          cap.dataset_ref,
          resolveByRef(cap.dataset_ref, cap.display_name)
        );
      }
    }
    if (dedupedByRef.size > 0) {
      return { names: [...dedupedByRef.values()], source: "site-local" };
    }
  }

  const legacyList = selectedDatasets || [];
  if (legacyList.length > 0) {
    const dedupedByRef = new Map();
    const unresolved = [];
    for (const entry of legacyList) {
      if (!entry) continue;
      const cap = (availableDatasets || []).find(
        (c) => c && c.capability_id === entry
      );
      if (cap?.dataset_ref) {
        if (!dedupedByRef.has(cap.dataset_ref)) {
          dedupedByRef.set(
            cap.dataset_ref,
            resolveByRef(cap.dataset_ref, cap.display_name)
          );
        }
      } else {
        // Not a known capability_id — probably a real file path from the
        // legacy simulation flow. Show the tail segment.
        const tail = String(entry).split("/").pop() || String(entry);
        unresolved.push(tail);
      }
    }
    const names = [...dedupedByRef.values(), ...unresolved];
    if (names.length > 0) return { names, source: "legacy" };
  }

  return { names: [], source: "empty" };
};
